import { useTranslation } from 'react-i18next';

import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useLocation, useNavigate, useSearchParams } from '@remix-run/react';
import cx from 'classnames';

import { useWebThemeContext } from '~/components/Layout/Context/useWebThemeContext';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import type { Publication } from '~/interfaces/publication';
import type { HomePageLoaderData } from '~/routes';

interface Props {
  setIsSearching: (isSearching: boolean) => void;
  isSearching: boolean;
  publication: Publication;
  searchTerm: string;
  setSearchTerm: (searchTerm: string) => void;
  isNewWebBuilder?: boolean;
}

// biome-ignore lint/correctness/noUnusedVariables: Keeping for backwards compatibility
const TagOption = ({ color, tag, setIsSearching, bgColor, isNewWebBuilder, radiusClass }: any) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const all = searchParams.getAll('tags');
  const value = tag.display;
  const checked = all.includes(value);
  const navigate = useNavigate();
  const location = useLocation();

  const handleChecked = () => {
    const newSearchParams = new URLSearchParams(searchParams);

    if (checked) {
      newSearchParams.delete('tags');
      for (const tag of all) {
        if (tag !== value) {
          newSearchParams.append('tags', tag);
        }
      }
    } else {
      newSearchParams.append('tags', value);
    }

    const paramsString = newSearchParams.toString();

    if (!location.pathname.includes('/archive')) {
      const basePath = '/archive';
      const newPath = paramsString ? `${basePath}?${paramsString}` : basePath;
      navigate(newPath);
    } else {
      setIsSearching(true);
      setSearchParams(newSearchParams);
    }
  };

  return (
    <button
      type="button"
      style={{
        borderColor: color,
        color: checked ? bgColor : color,
        backgroundColor: checked ? color : 'transparent',
      }}
      onClick={handleChecked}
      className={cx(radiusClass, 'cursor-pointer border px-4 py-1.5 text-xs')}
    >
      {value}
    </button>
  );
};

export const ClearAction = ({ setSearchTerm, color, classNames, showX }: any) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const hasParams = searchParams.has('q') || searchParams.has('tags');
  const { t } = useTranslation();

  const handleClearFilters = (_e: any) => {
    searchParams.delete('q');
    searchParams.delete('tags');
    setSearchTerm('');
    setSearchParams(searchParams);
  };

  if (!hasParams) return null;

  return (
    <button
      onClick={handleClearFilters}
      style={{
        borderColor: color,
        color: color,
      }}
      className={cx(classNames, 'flex items-center space-x-1 border px-4 py-1.5')}
    >
      {showX && <XMarkIcon className="mr-1 h-3 w-3" />}
      {t('search.posts.clear_filters')}
    </button>
  );
};

const ArchiveFilter = ({ searchTerm, setSearchTerm, isSearching, setIsSearching, isNewWebBuilder = false }: Props) => {
  const { classes } = useWebThemeContext();
  const { t } = useTranslation();
  const { homePage, publication } = useTypedLoaderData<HomePageLoaderData>();
  const color = homePage.general_section.theme.text_color;
  const bgColor = homePage.general_section.theme.bg_color;

  const handleChange = (e: any) => {
    setSearchTerm(e.target.value);

    if (!isSearching) {
      setIsSearching(true);
    }
  };

  return (
    <div className="mb-8">
      {/** Search */}
      <div className="relative w-full pb-4">
        {isNewWebBuilder ? (
          <div className="group relative w-full transition-all">
            <div className="absolute left-2 top-1/2 -translate-y-1/2 transform">
              <MagnifyingGlassIcon
                style={{
                  color: color,
                }}
                className="h-4 w-4"
              />
            </div>
            <input
              type="text"
              name="q"
              id="q"
              value={searchTerm}
              onChange={handleChange}
              style={{
                color: '#111827',
                border: '1px solid #F9FAFB',
                backgroundColor: '#F9FAFB',
              }}
              className={cx(classes.radius, 'w-full max-w-none pl-8 sm:max-w-4xl')}
              placeholder={`${t('search.posts.placeholder')}...` || ''}
            />
          </div>
        ) : (
          <div className="group relative w-full text-wt-text-on-background transition-all">
            <div className="absolute top-2.5">
              <MagnifyingGlassIcon
                style={{
                  color: color,
                }}
                className="h-4 w-4"
              />
            </div>
            <input
              type="text"
              name="q"
              id="q"
              value={searchTerm}
              onChange={handleChange}
              style={{
                color: color,
                borderBottomColor: color,
              }}
              className={
                'text-regular w-full rounded-none border-b border-transparent bg-transparent py-2 pl-6 text-sm outline-none transition-all focus:border-transparent focus:opacity-100'
              }
              placeholder={`${t('search.posts.placeholder')}...` || ''}
            />
          </div>
        )}
      </div>

      {/** Filter Tags */}
      <div className="mb-2 flex flex-wrap gap-2">
        {publication.content_tags?.map((tag: any) => {
          return (
            <TagOption
              key={tag.display}
              tag={tag}
              color={color}
              bgColor={bgColor}
              setIsSearching={setIsSearching}
              isNewWebBuilder={isNewWebBuilder}
              radiusClass={classes.radius}
            />
          );
        })}
      </div>

      {/** Clear Filters */}
      <ClearAction
        setSearchTerm={setSearchTerm}
        color={color}
        classNames={cx('px-2 py-1 text-xs', classes.radius)}
        showX={true}
      />
    </div>
  );
};

export default ArchiveFilter;
